import { dateAdd, formatDate, formatDateShort, formatMonth, formatMonthShort, formatWeekDayShort, getDayRange, getMonthRange, getWeekNumber, getWeekRange, getWeekYear, getYearRange, parseDateString, toDateString, } from "./date.util";
export function getRange(inp, period) {
    if (inp instanceof Date) {
        inp = toDateString(inp);
    }
    switch (period) {
        case "year":
            return getYearRange(inp);
        case "month":
            return getMonthRange(inp);
        case "week":
            return getWeekRange(inp);
        case "day":
            return getDayRange(inp);
    }
}
export function safeGetMonday(inp) {
    const date = parseDateString(inp);
    if (!date)
        return null;
    date.setDate(date.getDate() - ((date.getDay() + 6) % 7));
    return toDateString(date);
}
export function safeGetWeekRange(inp) {
    const from = safeGetMonday(inp);
    if (!from)
        return null;
    return {
        from,
        to: dateAdd(from, { days: 7 }),
    };
}
export function getSubRanges(range, resolution = "month") {
    const ranges = [];
    let from = range.from;
    while (from < range.to) {
        const subRange = {
            from,
            to: dateAdd(from, { [`${resolution}s`]: 1 }),
        };
        if (subRange.to > range.to) {
            subRange.to = range.to;
        }
        ranges.push(subRange);
        from = subRange.to;
    }
    return ranges;
}
export function splitRangeByResolution(range, resolution = "month") {
    const ranges = [];
    let date = range.from;
    while (date < range.to) {
        const currRange = getRange(date, resolution);
        ranges.push({
            from: range.from > currRange.from ? range.from : currRange.from, // get the max of the two dates
            to: range.to < currRange.to ? range.to : currRange.to, // get the min of the two dates
        });
        date = currRange.to;
    }
    return ranges;
}
export function intersectRanges(range1, range2) {
    const effectiveStart = range1.from > range2.from ? range1.from : range2.from;
    const effectiveEnd = range1.to < range2.to ? range1.to : range2.to;
    if (effectiveEnd <= effectiveStart) {
        return null;
    }
    return { from: effectiveStart, to: effectiveEnd };
}
export function getCurrentMonthRange() {
    return getRange(new Date(), "month");
}
export function inferDynamicDateRangeType(range) {
    const fromDate = parseDateString(range.from);
    const toDate = parseDateString(range.to);
    if (!fromDate || !toDate) {
        return "custom";
    }
    const differenceDays = Math.round((toDate.getTime() - fromDate.getTime()) / (1000 * 60 * 60 * 24)); // TODO: replace with `day` once import doesnt break tests anymore
    // check if it matches a full week
    if (differenceDays === 7) {
        return "week";
    }
    // check if it matches a full month
    const fromMonthDays = new Date(fromDate.getFullYear(), fromDate.getMonth() + 1, 0).getDate();
    if (differenceDays === fromMonthDays) {
        return "month";
    }
    // check if it matches a full year
    const isOneYear = toDate.getFullYear() - fromDate.getFullYear() === 1;
    const isSameDate = range.from.substring(5) === range.to.substring(5);
    if (isOneYear && isSameDate) {
        return "year";
    }
    return "custom";
}
export function inferRangeType({ from, to }) {
    // infer type based on our predefined ranges
    for (const type of ["month", "year", "week", "day"]) {
        const range = getRange(from, type);
        if (range.from === from && range.to === to) {
            return type;
        }
    }
    return "custom";
}
export function formatRange(range, rangeType = inferRangeType(range)) {
    const date = parseDateString(range.from);
    switch (rangeType) {
        case "year":
            return date.getFullYear().toString();
        case "month":
            return formatMonth(date);
        case "day":
            return formatDate(date);
        case "week":
            return `KW ${getWeekNumber(range.from)}, ${getWeekYear(range.from)} (${formatDateShort(range.from, false)} - ${formatDateShort(dateAdd(range.to, { days: -1 }), false)})`;
        default: {
            const from = range.from;
            const to = dateAdd(range.to, { days: -1 });
            return `${formatWeekDayShort(from)}, ${formatDate(from)} - ${formatWeekDayShort(to)}, ${formatDate(to)}`;
        }
    }
}
export function formatRangeShort(range, rangeType = inferRangeType(range)) {
    const date = parseDateString(range.from);
    if (!date) {
        throw new Error("Invalid date string");
    }
    switch (rangeType) {
        case "year":
            return date.getFullYear().toString();
        case "month":
            return formatMonthShort(date);
        case "day":
            return formatDateShort(date, true, true);
        case "week":
            return `(${formatDateShort(range.from, true, true)} - ${formatDateShort(dateAdd(range.to, { days: -1 }), true, true)})`;
        default: {
            const from = range.from;
            const to = dateAdd(range.to, { days: -1 });
            return `${formatDateShort(from, true, true)} - ${formatDateShort(to, true, true)}`;
        }
    }
}
//# sourceMappingURL=range.util.js.map